/**
 * Rotates a point around a given origin by a specified angle.
 * @param point The point to rotate
 * @param angle The angle in degrees to rotate the point
 * @param origin The origin point around which to rotate the point
 */
export function rotatePoint(
  point: { x: number; y: number },
  angle: number,
  origin: { x: number; y: number }
): { x: number; y: number } {
  const radians = (angle * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  const translatedX = point.x - origin.x;
  const translatedY = point.y - origin.y;

  return {
    x: translatedX * cos - translatedY * sin + origin.x,
    y: translatedX * sin + translatedY * cos + origin.y
  };
}

/**
 * Calculates the bounding box of a rectangle, taking into account its rotation.
 * @param rect The rectangle to calculate the bounding box for
 * @returns 
 */
export function getBoundingBox(rect: { x: number; y: number; width: number; height: number; rotation?: number }) {
  const { x, y, width, height, rotation = 0 } = rect;
  const center = { x: x + width / 2, y: y + height / 2 };

  const corners = [
    { x: x, y: y },
    { x: x + width, y: y },
    { x: x + width, y: y + height },
    { x: x, y: y + height }
  ];

  const rotatedCorners = corners.map(corner => rotatePoint(corner, rotation, center));

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  rotatedCorners.forEach(corner => {
    if (corner.x < minX) minX = corner.x;
    if (corner.y < minY) minY = corner.y;
    if (corner.x > maxX) maxX = corner.x;
    if (corner.y > maxY) maxY = corner.y;
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,

    left: minX,
    top: minY,
    right: maxX,
    bottom: maxY
  };
}

/**
 * Get the corners of a rotated box
 * @param box The box to get the corners for
 * @returns The corners of the rotated box
 */
export function getRotatedBoxCorners(box: { x: number; y: number; width: number; height: number; rotation?: number }) {
  const corners = [
    { x: box.x, y: box.y },
    { x: box.x + box.width, y: box.y },
    { x: box.x + box.width, y: box.y + box.height },
    { x: box.x, y: box.y + box.height }
  ];

  if (!box.rotation) {
    return corners;
  }

  const center = { x: box.x + box.width / 2, y: box.y + box.height / 2 };

  return corners.map(corner => rotatePoint(corner, box.rotation || 0, center));
}

/**
 * Checks if a point is inside a rotated box.
 * @param point The point to check
 * @param box The rotated box corners to check against
 */
export function isPointInRotatedBox(point: { x: number; y: number }, box: { x: number; y: number }[]) {
  const [a, b, c, d] = box;

  // Check if the point is inside the bounding box of the rotated box
  const minX = Math.min(a.x, b.x, c.x, d.x);
  const maxX = Math.max(a.x, b.x, c.x, d.x);
  const minY = Math.min(a.y, b.y, c.y, d.y);
  const maxY = Math.max(a.y, b.y, c.y, d.y);

  if (point.x < minX || point.x > maxX || point.y < minY || point.y > maxY) {
    return false;
  }

  // Use the ray-casting algorithm to determine if the point is inside the polygon
  let inside = false;
  for (let i = 0, j = box.length - 1; i < box.length; j = i++) {
    const xi = box[i].x, yi = box[i].y;
    const xj = box[j].x, yj = box[j].y;

    const intersect = ((yi > point.y) !== (yj > point.y)) &&
      (point.x < (xj - xi) * (point.y - yi) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  
  return inside;
}

/**
 * Checks if two lines intersect
 * @param lineA The first line
 * @param lineB The second line
 */
export function doLinesIntersect(lineA: { x1: number; y1: number; x2: number; y2: number }, lineB: { x1: number; y1: number; x2: number; y2: number }) {
  const det = (lineA.x2 - lineA.x1) * (lineB.y2 - lineB.y1) - (lineA.y2 - lineA.y1) * (lineB.x2 - lineB.x1);
  if (det === 0) return false; // Lines are parallel

  const lambda = ((lineB.y2 - lineB.y1) * (lineB.x2 - lineA.x1) + (lineB.x1 - lineB.x2) * (lineB.y2 - lineA.y1)) / det;
  const gamma = ((lineA.y1 - lineA.y2) * (lineB.x2 - lineA.x1) + (lineA.x2 - lineA.x1) * (lineB.y2 - lineA.y1)) / det;

  return lambda >= 0 && lambda <= 1 && gamma >= 0 && gamma <= 1;
}

export function linesFromVertices(vertices: { x: number; y: number }[]): { x1: number; y1: number; x2: number; y2: number }[] {
  const lines: { x1: number; y1: number; x2: number; y2: number }[] = [];
  for (let i = 0; i < vertices.length; i++) {
    const start = vertices[i];
    const end = vertices[(i + 1) % vertices.length];
    lines.push({ x1: start.x, y1: start.y, x2: end.x, y2: end.y });
  }
  return lines;
}

/**
 * Check if two boxes overlap, factoring in their rotation
 * @param box1
 * @param box2 
 */
export function doBoxesOverlap(box1: { x: number; y: number; width: number; height: number; rotation?: number }, box2: { x: number; y: number; width: number; height: number; rotation?: number }) {
  // Get the corners of each box
  const box1Corners = getRotatedBoxCorners(box1);
  const box2Corners = getRotatedBoxCorners(box2);

  const box1Lines = linesFromVertices(box1Corners);
  const box2Lines = linesFromVertices(box2Corners);

  for (const lineA of box1Lines) {
    for (const lineB of box2Lines) {
      if (doLinesIntersect(lineA, lineB)) {
        return true;
      }
    }
  }

  // Check if any corners are inside the other box
  for (const corner of box1Corners) {
    if (isPointInRotatedBox(corner, box2Corners)) {
      return true;
    }
  }
  for (const corner of box2Corners) {
    if (isPointInRotatedBox(corner, box1Corners)) {
      return true;
    }
  }

  return false;
}